import React from 'react';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import {
  Divider,
  HoverStyles,
  Text,
  TextButton,
  TextButtonPriority,
} from 'wix-ui-tpa/cssVars';
import ChevronDown from 'wix-ui-icons-common/on-stage/ChevronDownSmall';
import ChevronUp from 'wix-ui-icons-common/on-stage/ChevronUpSmall';
import { classes, st } from './ParticipantSection.st.css';
import { ParticipantSectionDataHooks } from './dataHooks';
import { getParticipantLabel } from '../../../../utils/multiParticipantFlow';

export interface ParticipantSectionProps {
  participantIndex: number;
  totalParticipants: number;
  isExpanded: boolean;
  onToggle: () => void;
  onRemove?: () => void;
  // Participant's name once the form has one; the header falls back to "Participant N".
  title?: string;
  children: React.ReactNode;
}

/**
 * One participant's slice of the booking form (form, price variant, add-ons, ...).
 *
 * With a single participant it renders its children as-is, so the form looks exactly like
 * today's. With several participants each section gets a header (the participant's name,
 * Remove, chevron) and collapses like an accordion. The body stays mounted while collapsed so its
 * form-viewer ref remains available for validation.
 */
const ParticipantSection = React.forwardRef<
  HTMLDivElement,
  ParticipantSectionProps
>(
  (
    {
      participantIndex,
      totalParticipants,
      isExpanded,
      onToggle,
      onRemove,
      title,
      children,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const participantLabel = getParticipantLabel(t, participantIndex + 1);
    const removeText = t(
      'bookings.form.collectParticipant.removeParticipant.text',
    );
    const { isRTL } = useEnvironment();
    const showHeader = totalParticipants > 1;
    const bodyId = `participant-section-body-${participantIndex}`;
    const rowLabel = [participantLabel, title].filter(Boolean).join(' ');
    const ToggleIcon = isExpanded ? ChevronUp : ChevronDown;

    return (
      <div
        ref={ref}
        className={st(classes.root, {
          isRTL,
          withHeader: showHeader,
          isExpanded,
        })}
        data-hook={`${ParticipantSectionDataHooks.ROOT}-${participantIndex}`}
      >
        {showHeader && participantIndex === 0 && (
          <Divider
            className={classes.leadingDivider}
            data-hook={ParticipantSectionDataHooks.LEADING_DIVIDER}
          />
        )}
        {showHeader && (
          <div
            className={classes.header}
            data-hook={ParticipantSectionDataHooks.HEADER}
          >
            <button
              type="button"
              className={classes.toggle}
              data-hook={ParticipantSectionDataHooks.TOGGLE}
              onClick={onToggle}
              aria-expanded={isExpanded}
              aria-controls={bodyId}
              aria-label={rowLabel}
            >
              <Text
                className={classes.title}
                data-hook={ParticipantSectionDataHooks.TITLE}
              >
                {title || participantLabel}
              </Text>
            </button>
            {onRemove && (
              <TextButton
                className={classes.remove}
                priority={TextButtonPriority.secondary}
                hoverStyle={HoverStyles.underline}
                // Built from the visible word plus the participant, so the accessible
                // name always contains what is on screen (WCAG 2.5.3) whatever the
                // translations turn out to be, and it says which participant.
                aria-label={`${removeText} ${rowLabel}`}
                data-hook={ParticipantSectionDataHooks.REMOVE}
                onClick={onRemove}
              >
                {removeText}
              </TextButton>
            )}
            {/* A second control for the same toggle: visitors click the chevron, so it
                cannot be a click target hidden from assistive tech either. It has the
                title button's name and stays out of the tab order, since the title button
                ahead of it already does the job for the keyboard. */}
            <button
              type="button"
              tabIndex={-1}
              className={classes.chevron}
              data-hook={ParticipantSectionDataHooks.CHEVRON}
              onClick={onToggle}
              aria-label={rowLabel}
              aria-expanded={isExpanded}
              aria-controls={bodyId}
            >
              <ToggleIcon />
            </button>
          </div>
        )}
        <div
          id={bodyId}
          className={classes.body}
          data-hook={ParticipantSectionDataHooks.BODY}
          hidden={showHeader && !isExpanded}
        >
          {children}
        </div>
        {showHeader && <Divider className={classes.divider} />}
      </div>
    );
  },
);

ParticipantSection.displayName = 'ParticipantSection';

export default ParticipantSection;
